import { TABLE_TYPE_SITNGO } from 'constants/gameConstants';
import {
  pickLiveTournamentRow,
  pickNewestTournamentRow,
  tournamentRowFreshness
} from 'utils/pickLiveTournamentRow';

import type { PayoutRow } from 'components/TournamentPayouts/payouts';

/**
 * EN-11257 (release.sit-go-seated-waiting): a Rolling Sit & Go before it
 * starts. The registrants are not seated at the table yet — the engine draws
 * the seats when the table fills — so the felt is empty and the table feed has
 * no p{n} rows. Each gotTnmStatus row of the tournament carries the drawn
 * seats instead (`waitingSeats`), and this module reads the felt's waiting
 * state from that row and the table's own status, nothing kept locally, so
 * leaving and reopening the table shows the same thing.
 */

/** One registrant in their drawn seat, as the felt shows them. */
export type WaitingRegistrant = {
  seat: number;
  playerId: string;
  displayName: string;
  /** Same format as players[n].avatar in the table feed. */
  avatar: string;
  stack: number;
};

export type SitGoWaitingState = {
  tournamentId: number;
  /** The table's seat count. */
  maxSeat: number;
  /** Registrants by seat (1..maxSeat). Empty until the backend sends them. */
  seats: ReadonlyMap<number, WaitingRegistrant>;
  /** The viewer's own drawn seat, when they are one of the registrants. */
  ownSeat: number | undefined;
  /** The viewer's own status row says they are registered and waiting. */
  isRegistered: boolean;
  /** Seats still to fill before the game starts. */
  openSeats: number;
  /** Stake plus fee, when the status row or the lobby row carries one. */
  buyIn: number | undefined;
};

// The table feed's status once the game is under way (or over). Any of them
// means the waiting data is history: the real seats own the felt.
const STARTED_STATUSES = new Set(['P', 'F', 'C']);

/** A finite number from a number or a money string ("$6", "6.00"), else undefined. */
export const toAmount = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === '') return undefined;
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  const parsed = parseFloat(String(value).replace(/[^0-9.-]/g, ''));
  return Number.isFinite(parsed) ? parsed : undefined;
};

const parseRegistrant = (raw: any, maxSeat: number): WaitingRegistrant | null => {
  const seat = Number(raw?.seat);
  if (!Number.isInteger(seat) || seat < 1 || seat > maxSeat) return null;
  const playerId = typeof raw?.playerId === 'string' ? raw.playerId : '';
  if (!playerId) return null;
  const stack = toAmount(raw?.stack);
  return {
    seat,
    playerId,
    displayName: typeof raw?.displayName === 'string' ? raw.displayName : '',
    avatar: typeof raw?.avatar === 'string' ? raw.avatar : '',
    stack: stack !== undefined && stack >= 0 ? stack : 0
  };
};

/**
 * The row's `waitingSeats`, validated: a seat outside 1..maxSeat, an entry
 * with no player, or a second entry for a seat already taken is dropped. A
 * missing or malformed field (the backend has not deployed) is no seats, which
 * renders today's Open seats.
 */
export const parseWaitingSeats = (
  raw: unknown,
  maxSeat: number
): Map<number, WaitingRegistrant> => {
  const seats = new Map<number, WaitingRegistrant>();
  if (!Array.isArray(raw) || maxSeat < 1) return seats;
  raw.forEach((entry) => {
    const registrant = parseRegistrant(entry, maxSeat);
    if (registrant && !seats.has(registrant.seat)) seats.set(registrant.seat, registrant);
  });
  return seats;
};

/** The status row's buy-in (buyIn + entryFee), else the lobby row's. */
export const resolveSitGoBuyIn = (row: any, lobbyRow: any): number | undefined => {
  const stake = toAmount(row?.buyIn);
  if (stake !== undefined) return stake + (toAmount(row?.entryFee) ?? 0);
  // The lobby's Sit & Go row carries the price as the engine broadcasts it,
  // stake and fee together.
  return toAmount(lobbyRow?.buyIn ?? lobbyRow?.entry);
};

export type SitGoWaitingInputs = {
  game: any;
  players: any[] | undefined;
  tournamentStatus: unknown;
  guid: string | null | undefined;
  sitAndGoList?: any[];
};

/**
 * Whether the table feed alone allows a filling Sit & Go: a Sit & Go that has
 * not started. Cheap, so subscribers can skip the status feed otherwise.
 */
export const isSitGoWaitingCandidate = (game: any): boolean =>
  game?.tabletype === TABLE_TYPE_SITNGO && !STARTED_STATUSES.has(game?.status);

/**
 * The status row the waiting felt reads: pickLiveTournamentRow's choice, but
 * the viewer's own row only while it is as fresh as the newest row. Once the
 * viewer unregisters, the server stops sending their own row and sends them
 * a row copied from a registrant instead; the reducer keeps both, so the old
 * own row would otherwise keep seating them and offering Unregister.
 */
export const pickWaitingRow = (
  rows: unknown,
  tournamentId: number,
  guid: unknown
): { row: any; usedOwn: boolean } | null => {
  const picked = pickLiveTournamentRow(rows, tournamentId, guid);
  if (!picked?.usedOwn) return picked;
  const newest = pickNewestTournamentRow(rows, tournamentId);
  return tournamentRowFreshness(newest) > tournamentRowFreshness(picked.row)
    ? { row: newest, usedOwn: false }
    : picked;
};

/**
 * The felt's waiting state, or null when the table is not a Sit & Go still
 * filling: not a Sit & Go, the tournament has started (the table feed's status,
 * or real seats arriving), or there is no live status row for it yet.
 */
export const deriveSitGoWaiting = ({
  game,
  players,
  tournamentStatus,
  guid,
  sitAndGoList
}: SitGoWaitingInputs): SitGoWaitingState | null => {
  if (!isSitGoWaitingCandidate(game)) return null;
  const tournamentId = Number(game?.tournamentId);
  if (!Number.isFinite(tournamentId) || tournamentId <= 0) return null;
  const tableSeats = Array.isArray(players) ? players.slice(1) : [];
  if (tableSeats.some((seat: any) => seat?.ID)) return null;

  const picked = pickWaitingRow(tournamentStatus, tournamentId, guid);
  if (!picked) return null;
  const { row, usedOwn } = picked;
  if ((row?.tStatus ?? game?.status) !== 'R') return null;

  const maxSeat =
    Number(row?.maxSeat) || Number(game?.maxSeat) || Number(game?.seats) || tableSeats.length;
  const seats = parseWaitingSeats(row?.waitingSeats, maxSeat);
  const registered = toAmount(row?.registeredPlayers) ?? seats.size;
  const ownSeat = guid
    ? Array.from(seats.values()).find((registrant) => registrant.playerId === guid)?.seat
    : undefined;
  const lobbyRow = (sitAndGoList || []).find(
    (entry: any) => Number(entry?.tournamentId) === tournamentId
  );

  return {
    tournamentId,
    maxSeat,
    seats,
    ownSeat,
    isRegistered: usedOwn && row?.status === 'R',
    openSeats: Math.max(0, maxSeat - registered),
    buyIn: resolveSitGoBuyIn(row, lobbyRow)
  };
};

/**
 * First place's cash prize from the full-table payout projection
 * (GET /sng/:id/payouts previews a registering Sit & Go at its full field), so
 * the felt shows what the winner will take, not the pool collected so far.
 * Undefined while it loads, when it is unavailable, or for a ticket prize.
 */
export const firstPlacePrize = (rows: readonly PayoutRow[]): number | undefined => {
  const first = rows.find((row) => row.from === 1);
  return first && !first.isTicket ? first.amount : undefined;
};

/**
 * The drawn seats as the table state shows them now, through the start:
 *  - a map: the latest status row lists the waiting seats;
 *  - null: stop holding drawn seats — not a Sit & Go, the game is over, or
 *    real seats have arrived;
 *  - undefined: no news — the latest row carries no `waitingSeats`. The
 *    backend stops sending them once the tournament leaves 'R', which can land
 *    before the table feed's real seats do.
 *
 * Unlike deriveSitGoWaiting this does not stop at 'P': the start reaches the
 * client as separate, unordered updates (status row, table status, real
 * seats), and the felt has to hold the drawn seats across all of them.
 */
export const readWaitingSeatsThroughStart = (
  table: any,
  guid: unknown
): Map<number, WaitingRegistrant> | null | undefined => {
  const game = table?.game;
  if (game?.tabletype !== TABLE_TYPE_SITNGO) return null;
  if (game?.status === 'F' || game?.status === 'C') return null;
  const tournamentId = Number(game?.tournamentId);
  if (!Number.isFinite(tournamentId) || tournamentId <= 0) return null;
  const tableSeats = Array.isArray(table?.players) ? table.players.slice(1) : [];
  if (tableSeats.some((seat: any) => seat?.ID)) return null;

  const picked = pickWaitingRow(table?.tournamentStatus, tournamentId, guid);
  if (!picked || !Array.isArray(picked.row?.waitingSeats)) return undefined;
  const maxSeat =
    Number(picked.row?.maxSeat) ||
    Number(game?.maxSeat) ||
    Number(game?.seats) ||
    tableSeats.length;
  return parseWaitingSeats(picked.row.waitingSeats, maxSeat);
};

/**
 * The viewer's drawn seat as the table state shows it now, for the layout: a
 * seat number when the latest row lists the viewer; null when the layout must
 * stop holding one (see readWaitingSeatsThroughStart, or the row lists the
 * waiting seats without the viewer: they unregistered, or they observe);
 * undefined for no news.
 */
export const readDrawnLayoutSeat = (table: any, guid: unknown): number | null | undefined => {
  if (typeof guid !== 'string' || !guid) return null;
  const seats = readWaitingSeatsThroughStart(table, guid);
  if (!(seats instanceof Map)) return seats;
  const own = Array.from(seats.values()).find((registrant) => registrant.playerId === guid);
  return own ? own.seat : null;
};

/** Drawn seats the felt keeps showing while the feed has no news. */
export type HeldWaitingSeats = {
  tournamentId: number;
  seats: ReadonlyMap<number, WaitingRegistrant>;
};

/**
 * The registrants to draw in empty seats, and what to keep holding: the
 * latest listed waiting seats, held from the last row that listed them until
 * real seats arrive or the table ends. So the drawn players stay in their
 * seats through the start, whichever part of it arrives first, instead of the
 * seats reading Open until the real seats land. A row that lists the waiting
 * seats again (one unregistered) replaces the hold.
 */
export const holdWaitingSeats = (
  tournamentId: number,
  read: ReadonlyMap<number, WaitingRegistrant> | null | undefined,
  held: HeldWaitingSeats | null
): { seats: ReadonlyMap<number, WaitingRegistrant> | undefined; held: HeldWaitingSeats | null } => {
  if (read === null) return { seats: undefined, held: null };
  if (read) return { seats: read, held: { tournamentId, seats: read } };
  if (held && held.tournamentId === tournamentId) return { seats: held.seats, held };
  return { seats: undefined, held: null };
};

/** A drawn seat the layout keeps holding while the feed has no news. */
export type HeldLayoutSeat = { tournamentId: number; seat: number };

/**
 * The seat the felt is laid out around, and what to keep holding: the
 * viewer's real seat once they have one; otherwise the seat they were drawn,
 * held from the last row that listed them until real seats arrive or a row
 * lists the waiting seats without them. So the drawn seat is at the bottom
 * while the table fills and nothing moves when the game seats them there,
 * whichever part of the start arrives first.
 */
export const holdLayoutSeat = (
  realSeatId: number,
  tournamentId: number,
  drawnSeat: number | null | undefined,
  held: HeldLayoutSeat | null
): { seatId: number; held: HeldLayoutSeat | null } => {
  if (realSeatId >= 1 || drawnSeat === null) return { seatId: realSeatId, held: null };
  if (typeof drawnSeat === 'number') {
    return { seatId: drawnSeat, held: { tournamentId, seat: drawnSeat } };
  }
  if (held && held.tournamentId === tournamentId) return { seatId: held.seat, held };
  return { seatId: realSeatId, held: null };
};

/** "Waiting for 1 more player" / "Waiting for N more players". */
export const waitingHeadline = (openSeats: number): string => {
  if (openSeats <= 0) return 'Starting now';
  return openSeats === 1 ? 'Waiting for 1 more player' : `Waiting for ${openSeats} more players`;
};
